import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent, ReactNode } from 'react';

type Owner = { id: symbol; preview: boolean; close: () => void };
type Group = {
  request: (id: symbol) => void;
  cancel: (id: symbol) => void;
  claim: (owner: Owner) => boolean;
  release: (id: symbol) => void;
};
const DisclosureContext = createContext<Group | null>(null);

export function HoverDisclosureGroup({ children }: { children: ReactNode }) {
  const owner = useRef<Owner | null>(null);
  const intent = useRef<symbol | null>(null);
  const group = useMemo<Group>(() => ({
    request(id) { intent.current = id; },
    cancel(id) { if (intent.current === id) intent.current = null; },
    claim(next) {
      if (next.preview && intent.current !== next.id) return false;
      const current = owner.current;
      if (current && current.id !== next.id) {
        if (!current.preview && next.preview) return false;
        current.close();
      }
      owner.current = next;
      return true;
    },
    release(id) { if (owner.current?.id === id) owner.current = null; },
  }), []);
  return <DisclosureContext.Provider value={group}>{children}</DisclosureContext.Provider>;
}

export function useHoverDisclosure(open: boolean, setOpen: (open: boolean) => void) {
  const group = useContext(DisclosureContext);
  const id = useRef(Symbol());
  const [preview, setPreview] = useState(false);
  const dismissed = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const opening = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const clear = useCallback(() => { clearTimeout(timer.current); clearTimeout(opening.current); }, []);
  const close = useCallback(() => { clear(); setPreview(false); setOpen(false); group?.release(id.current); }, [clear, setOpen, group]);
  useEffect(() => () => { clear(); group?.cancel(id.current); group?.release(id.current); }, [clear, group]);
  useEffect(() => { if (!open) { clear(); setPreview(false); group?.release(id.current); } }, [open, clear, group]);
  const claim = useCallback((isPreview: boolean) => !group || group.claim({ id: id.current, preview: isPreview, close }), [group, close]);
  const keep = useCallback(() => {
    clear();
    if (open) { group?.request(id.current); return; }
    if (claim(true)) { setPreview(true); setOpen(true); }
  }, [open, setOpen, clear, claim, group]);
  const leave = useCallback(() => {
    clear();
    group?.cancel(id.current);
    if (preview) timer.current = setTimeout(close, 140);
  }, [preview, close, clear, group]);
  const dismiss = useCallback(() => { dismissed.current = true; close(); }, [close]);
  const pin = useCallback(() => { dismissed.current = false; clear(); group?.request(id.current); claim(false); setPreview(false); setOpen(true); }, [setOpen, clear, claim, group]);
  const toggle = useCallback(() => {
    if (open && !preview) dismiss();
    else pin();
  }, [open, preview, dismiss, pin]);
  const onPointerEnter = useCallback((event: PointerEvent) => {
    if (event.pointerType === 'touch' || dismissed.current) return;
    clear();
    group?.request(id.current);
    if (!open) opening.current = setTimeout(keep, 100);
  }, [keep, clear, open, group]);
  const onPointerMove = useCallback((event: PointerEvent) => {
    if (event.pointerType !== 'touch' && dismissed.current) {
      dismissed.current = false;
      onPointerEnter(event);
    }
  }, [onPointerEnter]);
  return { preview, pin, toggle, keep, leave, dismiss, onPointerEnter, onPointerMove, onPointerLeave: leave };
}
